
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useEffect } from 'react';
import { Toaster } from '@/components/ui/sonner';
import Index from '@/pages/Index';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import Dashboard from '@/pages/Dashboard';
import OrganizationSetup from '@/pages/OrganizationSetup';
import PricingPlans from '@/pages/PricingPlans';
import PaymentSuccess from '@/pages/PaymentSuccess';
import NotFound from '@/pages/NotFound';
import UserInvitation from '@/pages/UserInvitation';
import { ThemeProvider } from './components/theme-provider';
import YouTubeAuthCallback from '@/pages/YouTubeAuthCallback';
import AuthCallback from '@/pages/AuthCallback';
import { AuthProvider } from '@/contexts/auth';
import ProductPage from '@/pages/ProductPage';
import ScalooVsHootsuite from '@/pages/ScalooVsHootsuite';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import ShopHomePage from '@/pages/ShopHomePage';
import ShopCategoriesPage from '@/pages/ShopCategoriesPage';
import { useDocumentTitle } from './hooks/useDocumentTitle';
import './styles/base.css';

function AppContent() {
  // Utiliser notre hook pour gérer le titre de la page dynamiquement
  useDocumentTitle('Scaloo');
  
  useEffect(() => {
    const linkElement = document.createElement('link');
    linkElement.rel = 'stylesheet';
    linkElement.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Poppins:wght@400;500;600;700&display=swap';
    document.head.appendChild(linkElement);
    
    const urlParams = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = urlParams.get('access_token');
    
    if (accessToken) {
      localStorage.setItem('google_access_token', accessToken);
      window.location.hash = '';
    }

    const storedTheme = localStorage.getItem("socialsync-theme");
    if (storedTheme === "dark" || 
        (!storedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
      document.body.style.backgroundColor = "hsl(222, 47%, 11%)";
    } else {
      document.body.style.backgroundColor = "#FAFBFD";
    }
  }, []);

  return (
    <Routes>
      <Route path="/" element={<Index />} />
      <Route path="/comparaison/hootsuite" element={<ScalooVsHootsuite />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/organization-setup" element={<OrganizationSetup />} />
      <Route path="/plans" element={<PricingPlans />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/invitation" element={<UserInvitation />} />
      <Route path="/youtube-auth-callback" element={<YouTubeAuthCallback />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/dashboard/*" element={<Dashboard />} />
      <Route path="/shop/:shopSlug" element={<ShopHomePage />} />
      <Route path="/shop/:shopSlug/categories" element={<ShopCategoriesPage />} />
      <Route path="/shop/:shopSlug/products/:productSlug" element={<ProductPage />} />
      <Route path="/:shopSlug/products/:productSlug" element={<ProductPage />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider defaultTheme="system" storageKey="socialsync-theme">
      <div className="dark-mode-transition min-h-screen bg-background text-foreground dark:bg-[#121624] font-sans">
        <AuthProvider>
          <Toaster 
            richColors 
            toastOptions={{
              classNames: {
                toast: "dark:bg-[#1e1e2d] dark:text-gray-100 dark:border dark:border-[rgba(60,63,84,0.6)]",
                title: "dark:text-gray-100 font-heading",
                description: "dark:text-gray-300",
                actionButton: "dark:bg-[#8b5cf6] dark:text-white",
                cancelButton: "dark:bg-gray-700 dark:text-gray-200",
              }
            }}
          />
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </AuthProvider>
      </div>
    </ThemeProvider>
  );
}

export default App;
